import React, { useState } from "react";
import styles from "./energy-requirements.module.scss";
import { Appliance } from "../../../../../model/appliance";
import { SelectedApplianceConfig } from "./energy-requirements.types";
import { MinusOnlyIcon, PlusOnlyIcon } from "../../../../../assets/icons";
import { Button } from "../../../components";

interface ConfigureApplianceProps {
	appliance: Appliance;
	existingConfig?: SelectedApplianceConfig;
	onSave: (config: SelectedApplianceConfig) => void;
	onCancel: () => void;
}

export const ConfigureAppliance: React.FC<ConfigureApplianceProps> = ({ appliance, existingConfig, onSave, onCancel }) => {
	const [quantity, setQuantity] = useState(existingConfig?.quantity ?? 1);
	const [dailyUsage, setDailyUsage] = useState(existingConfig?.dailyUsage ?? 1);
	const handleSave = () => {
		onSave({
			applianceId: appliance.id,
			applianceName: appliance.name,
			applianceIconUrl: appliance.iconUrl,
			appliancePower: appliance.power,
			quantity,
			dailyUsage,
		});
	};

	const incrementQuantity = () => setQuantity(prev => prev + 1);
	const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));
	const incrementDailyUsage = () => setDailyUsage(prev => Math.min(24, prev + 1));
	const decrementDailyUsage = () => setDailyUsage(prev => Math.max(1, prev - 1));

	return (
		<>
			<div className={styles.container}>
				<div className={styles.content}>
					<div className={styles.header}>
						<div className={styles.iconWrapper}>
							<span className={styles.questionMark}>&#10067;</span>
						</div>
						<div className={styles.titleSection}>
							<h2 className={styles.title}>Select Quantity & Usage</h2>
							<p className={styles.subtitle}>Add the quantity of the selected item</p>
						</div>
					</div>

					<div className={styles.applianceChip}>
						{appliance.iconUrl && <img src={appliance.iconUrl} alt={appliance.name} className={styles.applianceChipIcon} />}
						<span className={styles.applianceChipName}>{appliance.name}</span>
					</div>

					<div className={styles.formFields}>
						<div className={styles.formField}>
							<div className={styles.fieldLabelGroup}>
								<span className={styles.fieldLabel}>Quantity</span>
								<span className={styles.fieldHelper}>The number of {appliance.name.toLowerCase()}s to be used</span>
							</div>
							<div className={styles.fieldInputRow}>
								<div className={styles.inputContainer}>
									<div className={styles.inputValue}>
										<input
											type="number"
											className={styles.numberInput}
											value={quantity}
											onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
											min={1}
										/>
									</div>
								</div>
								<div className={styles.controlGroup}>
									<button className={styles.controlButton} onClick={decrementQuantity}>
										<MinusOnlyIcon />
									</button>
									<button className={styles.controlButton} onClick={incrementQuantity}>
										<PlusOnlyIcon />
									</button>
								</div>
							</div>
						</div>

						<div className={styles.formField}>
							<div className={styles.fieldLabelGroup}>
								<span className={styles.fieldLabel}>Daily usage (Hrs)</span>
								<span className={styles.fieldHelper}>How long will this device be in use</span>
							</div>
							<div className={styles.fieldInputRow}>
								<div className={styles.inputContainer}>
									<div className={styles.inputValue}>
										<input
											type="number"
											className={styles.numberInput}
											value={dailyUsage}
											onChange={e => setDailyUsage(Math.min(24, Math.max(1, parseInt(e.target.value) || 1)))}
											min={1}
											max={24}
										/>
									</div>
									<div className={styles.inputSuffix}>hrs</div>
								</div>
								<div className={styles.controlGroup}>
									<button className={styles.controlButton} onClick={decrementDailyUsage}>
										<MinusOnlyIcon />
									</button>
									<button className={styles.controlButton} onClick={incrementDailyUsage}>
										<PlusOnlyIcon />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className={styles.configureFooter}>
				<div className={styles.configureFooterInner}>
					<Button onClick={onCancel} variant="secondary">
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={quantity < 1 || dailyUsage < 1} variant="primary">
						Save
					</Button>
				</div>
			</div>
		</>
	);
};
